"use client";

import type { PageMapEntry } from "@server/requests/contentful/getPageByType";
import {
  createContext,
  use,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

type PageCatalogEntries = Array<[string, PageMapEntry]>;

type PageCatalogContextValue = {
  pages: Map<string, PageMapEntry> | undefined;
  setPages: (pages: Map<string, PageMapEntry> | undefined) => void;
};

const PageCatalogContext = createContext<PageCatalogContextValue | undefined>(
  undefined,
);

export function PageCatalogProvider({ children }: PropsWithChildren) {
  const [pages, setPages] = useState<Map<string, PageMapEntry> | undefined>();

  const value = useMemo(() => ({ pages, setPages }), [pages]);

  return (
    <PageCatalogContext.Provider value={value}>
      {children}
    </PageCatalogContext.Provider>
  );
}

export function usePageCatalog() {
  const context = useContext(PageCatalogContext);

  if (context === undefined) {
    throw new Error("usePageCatalog must be used within a PageCatalogProvider");
  }

  return context.pages;
}

type PageCatalogReaderProps = {
  pagesPromise: Promise<PageCatalogEntries>;
};

export default function PageCatalogReader(props: PageCatalogReaderProps) {
  const { pagesPromise } = props;
  const entries = use(pagesPromise);
  const setPages = useContext(PageCatalogContext)?.setPages;

  useEffect(() => {
    setPages?.(new Map(entries));
  }, [entries, setPages]);

  return null;
}
